import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { UserMenu } from '../../components/UserMenu.tsx';
import { ThemeToggle } from '../../components/ui/ThemeToggle.tsx';
import { useAuth } from '../../hooks/useAuth.tsx';
import { api } from '../../lib/api.ts';

/**
 * El marco del alta: la misma barra de la app, sin la navegación de operador.
 *
 * Hasta hoy el alta se renderizaba pelada sobre el fondo. Alguien que acaba de
 * entrar con su cuenta ve una página suelta y no sabe si está adentro del
 * producto: ni marca, ni forma de cerrar sesión, ni nada que le diga dónde está.
 *
 * **Pero no lleva la nav del operador**, y es a propósito: `Tenants`,
 * `Conversaciones`, `Contactos`, `Tickets` y `Métricas` son pantallas vacías o
 * ajenas para alguien que todavía no salió en vivo. «Tenants» además es un
 * concepto nuestro — el dueño tiene UN negocio, no una lista.
 *
 * Lo que sí necesita: saber que está en Tibo, ver de qué negocio, y poder
 * salir. Eso es lo que hay acá.
 */

interface ShellDelAltaProps {
  children: ReactNode;
}

export function ShellDelAlta({ children }: ShellDelAltaProps) {
  const { user } = useAuth();
  const tenantId = user?.tenantId ?? undefined;
  // La misma `queryKey` que usa la página: react-query la deduplica, así que
  // esto no agrega un pedido — lee el que ya está en vuelo.
  const { data } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.tenants.get(tenantId as string),
    enabled: Boolean(tenantId),
  });
  const negocio = data?.tenant?.name;

  return (
    <div className="flex min-h-screen flex-col bg-surface-app">
      <header
        data-testid="alta-shell"
        // Mismas medidas y tokens que `TopBar`: es la misma barra, no una
        // parecida. Que se vean distintas sería el problema que esto arregla.
        className="flex h-14 shrink-0 items-center border-b border-edge-subtle bg-surface-base px-6"
      >
        <div className="mr-8 flex items-center gap-2 font-semibold text-content-brand">
          {/* `alt=""` porque el texto de al lado ya dice el nombre: leerlo dos
              veces es lo que hace un lector de pantalla si no. */}
          <img src="/tibo-icon.svg" alt="" aria-hidden="true" className="h-7 w-7 shrink-0" />
          <span>Tibo</span>
        </div>

        {negocio && (
          <span
            className="flex-1 truncate text-sm text-content-secondary"
            data-testid="alta-negocio"
          >
            {negocio}
          </span>
        )}
        {!negocio && <span className="flex-1" />}

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <main id="main" className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
